import {
  CertFormSchema,
  CertificationIndexResponse,
  CertSchemaField,
  fieldKeyCodec,
  getCertFormDateData,
  getCertFormDateSchema,
  getCertFormUserData,
  getCertFormUserSchema,
  SignatureGroupType,
} from "@stormwater-software/lib-certification";
import {
  FormContext,
  FormContextProvider,
  FormContextValue,
  FormSchemaFields,
} from "@stormwater-software/lib-form";
import { UIControlType } from "@stormwater-software/lib-form-control-types";
import { map as bluebirdMap } from "bluebird";
import { find, get } from "lodash";
import PropTypes from "prop-types";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "react-query";
import AppContext from "../../../contexts/AppContext";
import DashboardContext from "../../../contexts/DashboardContext";
import { ProjectContext } from "../../../contexts/ProjectContext";
import { getInspectionTypeLabel } from "../../../utils";
import {
  BulkCertifyParam,
  certificationApi,
} from "../../../utils/api/certification";
import ComplianceCertificationInstructions from "../../Inspections/Certification/ComplianceCertificationInstructions";
import InspectionCertificationStatement from "../../Inspections/Certification/InspectionCertificationStatement";
import SignatureCheckbox from "../../Inspections/Certification/SignatureCheckbox";
import FormModal from "../../Shared/form/modal/FormModal";
import LoadingModal from "../../Shared/Loading/LoadingModal";
import { InspectionWithFindingCount } from "../../../hooks/customDashboard";
import inspectionApi from "../../../utils/api/inspection";
import {
  getCertLineDateBounds,
  getCertLineDefaultDate,
  resolveProjectTimezone,
} from "../../../utils/certDefaultDate";
import {
  getRequiredFindingCompletion,
  selectRequiredForCertFindingsFromRow,
} from "../../../utils/certRequiredFindings";

/** prepare schema data for api request */
function extractSchemaData(
  formData: Record<string, any>,
  state: { id: number; certState: CertificationIndexResponse }[],
): BulkCertifyParam {
  return state.reduce<BulkCertifyParam>((data, { id, certState }) => {
    certState.signatureGroups[SignatureGroupType.compliance].forEach(
      ({ type: lineType }) => {
        const key = fieldKeyCodec.encode(lineType, CertSchemaField.date, id);
        const date = get(formData, key, null);

        if (date) {
          data.push({ id, date, lineType });
        }
      },
    );

    return data;
  }, []);
}

function getLabelSchema(label: string, isHeading?: boolean) {
  return {
    label,
    controlType: UIControlType.plainText,
    className: isHeading ? "modal-h3" : undefined,
  };
}

/**
 * Certify multiple inspections.
 */
const BulkCertifyModal = ({
  handleSaveAnimation,
  hideModal,
  inspections,
  onSubmit,
  fromWidget
}: {
  handleSaveAnimation: () => void;
  hideModal: () => void;
  inspections: (Omit<InspectionWithFindingCount, "id"> & { id: number })[];
  updateInspections?: () => void;
  onSubmit: () => void;
  fromWidget?: boolean;
}) => {
  const appContext = useContext(AppContext);
  const user = appContext.get("user");
  const projectStore = useContext(ProjectContext);
  const { inspectionsCertDue, fetchStatsFuncForID } = useContext(DashboardContext);
  const [error, setError] = useState(false)
  const [inspectionName, setInspectionName] = useState<any[]>([])
  const inspectionIds = inspections.map(_ => _.id);
  const [selectedLabel, setSelectedLabel] = useState<string>()
  const [allLabels, setAllLabels] = useState<string[]>([])
  const query = useQuery({
    queryKey: ["cert", ...inspectionIds],
    queryFn: () =>
      bluebirdMap(
        inspectionIds,
        async id => {
          return {
            id,
            certState: await certificationApi.index(id),
          };
        },
        { concurrency: 4 },
      ),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  /**
   * SW2-2239: the `findingTypesWithTemplate` query was removed. It fetched EVERY finding type in the
   * tenant on each modal open, purely to answer "does this template declare a required-for-cert type" —
   * the wrong question. The agreed semantic looks at whether an actual finding on this inspection is
   * required for cert, which the row data already carries.
   *
   * The tenant backdating flag is fetched instead, which this modal previously had no access to: it
   * consumes only AppContext / ProjectContext / DashboardContext, never InspectionContext, so
   * `disable_inspection_backdating` was unenforced here even for tenants that had set it.
   */
  const backdatingQuery = useQuery({
    queryKey: ["tenantBackdatingDetails"],
    queryFn: () => inspectionApi.getTenantBackdatingDetails(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const isInspectionBackdatingDisabled = !!(backdatingQuery.data as any)
    ?.disable_inspection_backdating;
  const [checked, setChecked] = useState(false);



  const handleSubmit = useCallback(
    async (formData, formContext: FormContextValue) => {
      let updatedData = query.data

      if (!checked) {
        formContext.setError(
          "Please complete the Electronic Authorization Signature",
        );

        return;
      }

      if (selectedLabel && updatedData) {
        updatedData = updatedData.map((element) => {
          element.certState.signatureGroups.compliance = element.certState.signatureGroups.compliance.filter((ele: any) => {
            if (selectedLabel === "ALL Signatures") {
              return true
            }

            if (selectedLabel === "NO Label") {
              return (ele.label === null || ele.label === "")
            }

            return ele.label === selectedLabel
          })

          return element
        })
      }

      return certificationApi
        .bulkCertify(extractSchemaData(formData, updatedData || []))
        .then(() => {
          onSubmit();
          inspectionsCertDue.refetch()
          fetchStatsFuncForID.refetch()
          hideModal();
          handleSaveAnimation();
        });
    },
    [checked, query.data, projectStore],
  );

  /**
   * SW2-2239. `isFindingRequired`, `findingsRequiredForCompliance`, `latestFindingClosureDate` and
   * `complianceDefaultDate` were removed from here in favour of the shared resolver in
   * `utils/certDefaultDate` + `utils/certRequiredFindings`. Four issues went with them:
   *
   *  1. `complianceDefaultDate` read `signatureGroups.compliance[0].defaultDateLogic` and applied the
   *     first line's setting to every line in the group; the column is per line.
   *  2. `isFindingRequired` asked whether the TEMPLATE declares a required type, never looking at the
   *     inspection's findings — the opposite semantic to the Certification tab, so the two surfaces
   *     disagreed even when their date arithmetic matched. The tab's semantic is the agreed one.
   *  3. `latestFindingClosureDate` passed `inspections[0].inspection_template` — the FIRST inspection in
   *     the selection rather than the matched one — leaking one inspection's template across a
   *     multi-select bulk certify. Resolved by construction now that the matched row is passed in.
   *  4. It also depended on `findingTypesWithTemplateQuery`, which fetched every finding type in the
   *     tenant on each open. That query is gone.
   *
   * The picker's `minDate` is resolved per line here too. Previously every bulk modal passed a bare
   * `new Date(certState.minCertificationDate)` with no timezone correction, which permitted one calendar
   * day earlier than the server's stated minimum in every US timezone.
   */
  const timezone = resolveProjectTimezone(
    (query.data as any)?.[0]?.certState?.projectTimezone,
    (projectStore as any)?.project?.timezone,
  );

  /**
   * Per-inspection required-finding completion, keyed by inspection id.
   *
   * `inspections` is a freshly filtered array on every parent render, so it must never be a memo
   * dependency — see the note on `initialValue` below.
   */
  const completionByInspection = useMemo(() => {
    const map: Record<number, Date | null> = {};

    (inspections ?? []).forEach((row: any) => {
      map[row.id] = getRequiredFindingCompletion(
        selectRequiredForCertFindingsFromRow(row),
        timezone,
      );
    });

    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspections, timezone]);

  /** Value-stable fingerprint of the above, safe to use as a memo dependency. */
  const completionFingerprint = Object.entries(completionByInspection)
    .map(([id, date]) => `${id}:${date ? date.getTime() : ''}`)
    .join('|');

  /** The date and bounds for one line of one inspection. */
  const lineDateArgs = (certState: any, id: number, line: any) => ({
    group: 'compliance' as const,
    defaultDateLogic: line.defaultDateLogic,
    inspectionDOI: certState.inspectionDOI,
    timezone,
    requiredFindingCompletion: completionByInspection[id] ?? null,
    disableInspectionBackdating: isInspectionBackdatingDisabled,
  });

  const schema = useMemo(() => {
    const sch: CertFormSchema = {};

    if (query.data && query.data.length) {
      const firstGroups = query.data[0].certState.signatureGroups;
      const firstLine =
        firstGroups.compliance.length > 0
          ? firstGroups.compliance[0]
          : firstGroups.inspection.length > 0
            ? firstGroups.inspection[0]
            : undefined;

      if (firstLine !== undefined) {
        // name and position fields (first inspection & signature line only)
        Object.assign(
          sch,
          getCertFormUserSchema({
            encodeKey: (type, field) =>
              fieldKeyCodec.encode(type, field, inspectionIds[0]),
            line: firstLine,
          }),
        );
      }

      // date field, each inspection, each line
      query.data.forEach(({ certState, id }) => {
        const insp = find(inspections, { id });

        if (!certState.groupOptions[
          SignatureGroupType.compliance
        ].enabled) {
          setError(true)
          const abc: any[] = inspectionName

          insp && abc.push(`${insp.created_date} | ${insp}`)
          setInspectionName(abc)
        }

        let lines = certState.signatureGroups.compliance.filter(line => {
          return (
            find(certState.userSignatures.compliance, {
              lineType: line.type,
            }) === undefined
          );
        });

        //adding all labels in the state for filtering based on Label
        lines.forEach(ele => {
          let displayLabel = ele.label

          if (ele.label === null || ele.label === "") {
            displayLabel = "NO Label"
          }

          setAllLabels(prevState => {
            if (prevState.includes(`${displayLabel}`)) {
              return [...prevState]
            }
            else {
              return [...prevState, `${displayLabel}`]
            }
          })
        })

        if (selectedLabel) {
          lines = lines.filter(ele => {
            if (selectedLabel === "ALL Signatures") {
              return true
            }

            if (selectedLabel === "NO Label") {
              return (ele.label === null || ele.label === "")
            }

            return ele.label === selectedLabel
          })
        }

        if (insp && certState.groupOptions.compliance.enabled && lines.length) {
          Object.assign(sch, {
            [`${id}-label`]: getLabelSchema(getInspectionTypeLabel(insp, fromWidget), true),
          });

          lines.forEach(line => {
            const dateSchema = getCertFormDateSchema({
              enabled: true,
              line,
              // SW2-2239: per line, and timezone-corrected. Was
              // `new Date(certState.minCertificationDate)` — one value for the whole group, with no
              // timezone handling, so the picker allowed one calendar day earlier than the server's
              // minimum in every US zone. `maxDate` needs no change: lib-certification already pins it
              // to today.
              minDate: getCertLineDateBounds(lineDateArgs(certState, id, line))
                .minDate,
              encodeKey: (type, field) => fieldKeyCodec.encode(type, field, id),
              label: line.label ? `${line.label} Date` : "Signature Date",
            });
            const [dateFieldKey] = Object.keys(dateSchema);

            Object.assign(dateSchema[dateFieldKey].style, {
              // flex: "0 1 20rem",
              flex: "0 1 100%",
              // minWidth: "20rem",
              // display: "flex",
              // flexFlow: "row nowrap",
            });

            Object.assign(dateSchema[dateFieldKey], {
              isClearable: true,
            });

            Object.assign(sch, dateSchema);
          });
        }
      });
    }

    return sch;
  }, [query.data, selectedLabel]);


  const initialValue = useMemo(() => {
    const data = {};

    if (query.data) {
      query.data.forEach(({ certState, id }) => {
        if (
          certState.groupOptions.compliance.enabled &&
          certState.signatureGroups.compliance.length
        ) {
          const lines = certState.signatureGroups.compliance.filter(line => {
            return (
              find(certState.userSignatures.compliance, {
                lineType: line.type,
              }) === undefined
            );
          });

          lines.forEach(line => {
            Object.assign(
              data,
              getCertFormDateData({
                encodeKey: (type: string, field: CertSchemaField) =>
                  fieldKeyCodec.encode(type, field, id),
                lineType: line.type,
                value: getCertLineDefaultDate(
                  lineDateArgs(certState, id, line),
                ),
              }),
              getCertFormUserData({
                encodeKey: (type: string, field: CertSchemaField) =>
                  fieldKeyCodec.encode(type, field, id),
                lineType: line.type,
                value: {
                  name: user.name,
                  role: user.position || user.roleName,
                },
              }),
            );
          });
        }
      });
    }

    return data;
    /**
     * SW2-2239. `completionFingerprint` is a value-stable string, deliberately, NOT `inspections`.
     *
     * `FormContextProvider` re-writes every field whenever `initialValue`'s identity changes, and
     * `inspections` is a freshly filtered array on each parent render (Inspections.tsx / CertificationsDue
     * .tsx). Depending on it would re-apply `initialValue` continuously and stomp the user's edits — in
     * the modal whose entire purpose is editing those dates.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data, completionFingerprint, timezone, isInspectionBackdatingDisabled]);

  useEffect(
    () => () => {
      query.remove();
    },
    [],
  );

  if (!query.data || query.isLoading) {
    return <LoadingModal show />;
  }

  return (
    <FormContextProvider initialValue={initialValue}>
      <FormContext.Consumer>
        {formContext => (
          <FormModal
            modalProps={{
              title: "Certify Inspections",
              subTitle: (!fromWidget) ? `Compliance Certification for ${(projectStore as any).projectName
                }` : "",
              submitBtnText: "Certify",
              isExtraWide: true,
            }}
            onCancel={hideModal}
            onSubmit={formData => handleSubmit(formData, formContext)}
          >
            <section className="bulk-cert-modal">
              <div className="bulk-cert-modal__filter-signature">
                <span>Filter Signature :</span>
                <select
                  name="label-type"
                  onChange={(event) => {
                    setSelectedLabel(event.target.value)
                  }}
                >
                  <option value="-10" hidden>Select Label</option>
                  <option value="ALL Signatures">ALL Signatures</option>
                  {
                    allLabels.length ? allLabels.map((ele, index) => {
                      return (
                        <option value={ele} key={index}>{ele}</option>
                      )
                    }) :
                      <option value="-10" disabled>No Labels Found</option>
                  }
                </select>
              </div>

              <div className="bulk-cert-modal__form-schema-fields">
                <FormSchemaFields
                  schema={schema}
                  onChange={formContext.set}
                  formData={formContext.value}
                />
              </div>

              {error ?
                <p className="error-text">
                  {
                    inspectionName.map(insp => (
                      <div>
                        <strong>{insp}</strong>
                      </div>
                    ))
                  }
                  ** The following inspection cannot be certified because it has pending inspection certificate
                </p>
                :
                null
              }
              <ComplianceCertificationInstructions />
              <InspectionCertificationStatement
                attestations={
                  query.data[0].certState.attestations[
                  SignatureGroupType.compliance
                  ]
                }
              />
              <SignatureCheckbox
                enabled={
                  query.data[0].certState.groupOptions[
                    SignatureGroupType.compliance
                  ].enabled
                }
                errors={
                  query.data[0].certState.groupOptions[
                    SignatureGroupType.compliance
                  ].errors
                }
                checked={checked}
                // readOnly={checked}
                // onSubmit={() =>
                //   // formContext.getTransformedData().then((data: any) =>
                //   //   certContext.onSave({
                //   //     group: type,
                //   //     lineType: line.type,
                //   //     formData: data,
                //   //   }),
                //   // )
                // }
                onSubmit={async () => {
                  formContext.setError(null);
                  setChecked(!checked);
                }}
              />
            </section>
          </FormModal>
        )}
      </FormContext.Consumer>
    </FormContextProvider>
  );
};

BulkCertifyModal.propTypes = {
  handleSaveAnimation: PropTypes.func.isRequired,
  hideModal: PropTypes.func.isRequired,
  /** List of selected inspections. Each with a minimum certification date */
  inspections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      minCertificationDate: PropTypes.string,
      // created_date: PropTypes.string.isRequired,
      // and others...
    }),
  ).isRequired,
  updateInspections: PropTypes.func,
};

export default BulkCertifyModal;
